import React, { useEffect, useState, useCallback, useRef } from "react";
import { apiClient, formatSAR } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { Megaphone, Plus, Trash2, Edit3, TrendingUp, Eye, MousePointerClick, X, Upload, Loader2 } from "lucide-react";

const PLACEMENTS = [
  { value: "homepage_top", ar: "أعلى الرئيسية", en: "Homepage Top" },
  { value: "homepage_mid", ar: "وسط الرئيسية", en: "Homepage Middle" },
  { value: "homepage_bottom", ar: "أسفل الرئيسية", en: "Homepage Bottom" },
  { value: "sidebar", ar: "الشريط الجانبي", en: "Sidebar" },
  { value: "search_results", ar: "نتائج البحث", en: "Search Results" },
  { value: "listing_detail", ar: "صفحة الإعلان", en: "Listing Detail" },
];

const STATUSES = ["draft", "scheduled", "active", "paused", "expired", "cancelled"];

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

const emptyForm = () => ({
  advertiser_name: "",
  advertiser_logo: "",
  advertiser_email: "",
  advertiser_phone: "",
  title: "",
  subtitle: "",
  image: "",
  link: "",
  cta_label: "اعرف المزيد",
  placement: "homepage_mid",
  start_date: today(),
  end_date: addDays(30),
  price_sar: 5000,
  paid: false,
  invoice_ref: "",
  status: "draft",
  priority: 0,
});

const STATUS_STYLES = {
  active: { bg: "#16a34a20", border: "#16a34a", text: "#15803d" },
  scheduled: { bg: "#c9a55c20", border: "#c9a55c", text: "#8a6f2e" },
  paused: { bg: "#f5a72420", border: "#f5a724", text: "#a16207" },
  expired: { bg: "#6b6b6b20", border: "#6b6b6b", text: "#3a3a3a" },
  draft: { bg: "#3a3a3a10", border: "#3a3a3a30", text: "#6b6b6b" },
  cancelled: { bg: "#dc262620", border: "#dc2626", text: "#b91c1c" },
};

export default function AdCampaignsTab() {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ revenue_sar: 0, impressions: 0, clicks: 0, ctr_pct: 0 });
  const [filter, setFilter] = useState({ status: "", placement: "" });
  const [editing, setEditing] = useState(null); // null = closed, {} = create, {...} = edit
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (filter.status) params.status = filter.status;
    if (filter.placement) params.placement = filter.placement;
    const { data } = await apiClient.get("/admin/ad-campaigns", { params });
    setItems(data.items || []);
    setStats(data.stats || {});
  }, [filter.status, filter.placement]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(emptyForm());
    setError("");
  };
  const openEdit = (c) => {
    const copy = { ...c };
    setEditing(copy);
    setError("");
  };
  const close = () => {
    setEditing(null);
    setError("");
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editing.id) {
        const { id, effective_status, impressions, clicks, created_at, created_by, ...patch } = editing;
        await apiClient.patch(`/admin/ad-campaigns/${id}`, patch);
      } else {
        await apiClient.post("/admin/ad-campaigns", editing);
      }
      close();
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "Error");
    }
  };

  const del = async (id) => {
    if (!window.confirm(lang === "ar" ? "حذف الحملة الإعلانية؟" : "Delete campaign?")) return;
    await apiClient.delete(`/admin/ad-campaigns/${id}`);
    load();
  };

  const togglePaid = async (c) => {
    await apiClient.patch(`/admin/ad-campaigns/${c.id}`, { paid: !c.paid });
    load();
  };

  const setStatus = async (c, status) => {
    await apiClient.patch(`/admin/ad-campaigns/${c.id}`, { status });
    load();
  };

  return (
    <div data-testid="admin-ads" className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-display text-2xl flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-[#8a6f2e]" />
          {lang === "ar" ? "الحملات الإعلانية التجارية" : "Commercial Ad Campaigns"}
        </h2>
        <button onClick={openCreate} className="btn-primary text-sm" data-testid="ad-create-btn">
          <Plus className="w-4 h-4" /> {lang === "ar" ? "حملة إعلانية جديدة" : "New campaign"}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="surface rounded-xl p-5">
          <div className="text-xs text-[#6b6b6b] uppercase tracking-widest flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {lang === "ar" ? "إجمالي الإيرادات" : "Total revenue"}</div>
          <div className="font-display text-3xl mt-2 text-[#0a0a0a]">{formatSAR(stats.revenue_sar || 0)}</div>
          <div className="text-xs text-[#8a6f2e] mt-1">SAR</div>
        </div>
        <div className="surface rounded-xl p-5">
          <div className="text-xs text-[#6b6b6b] uppercase tracking-widest flex items-center gap-1"><Eye className="w-3 h-3" /> {lang === "ar" ? "مرات الظهور" : "Impressions"}</div>
          <div className="font-display text-3xl mt-2 text-[#0a0a0a]">{(stats.impressions || 0).toLocaleString()}</div>
        </div>
        <div className="surface rounded-xl p-5">
          <div className="text-xs text-[#6b6b6b] uppercase tracking-widest flex items-center gap-1"><MousePointerClick className="w-3 h-3" /> {lang === "ar" ? "النقرات" : "Clicks"}</div>
          <div className="font-display text-3xl mt-2 text-[#0a0a0a]">{(stats.clicks || 0).toLocaleString()}</div>
        </div>
        <div className="surface rounded-xl p-5">
          <div className="text-xs text-[#6b6b6b] uppercase tracking-widest">CTR</div>
          <div className="font-display text-3xl mt-2 text-[#0a0a0a]">{stats.ctr_pct || 0}<span className="text-base text-[#8a8a8a]">%</span></div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <select className="input-dark max-w-xs" value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} data-testid="ad-filter-status">
          <option value="">{lang === "ar" ? "كل الحالات" : "All statuses"}</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="input-dark max-w-xs" value={filter.placement} onChange={(e) => setFilter({ ...filter, placement: e.target.value })} data-testid="ad-filter-placement">
          <option value="">{lang === "ar" ? "كل المواقع" : "All placements"}</option>
          {PLACEMENTS.map(p => <option key={p.value} value={p.value}>{lang === "ar" ? p.ar : p.en}</option>)}
        </select>
      </div>

      {/* List */}
      {items.length === 0 ? (
        <div className="surface rounded-xl p-12 text-center text-[#6b6b6b]">
          <Megaphone className="w-10 h-10 mx-auto text-[#c9a55c] opacity-50 mb-3" />
          {lang === "ar" ? "لا توجد حملات إعلانية. أنشئ أول حملة لشركة معلِنة." : "No campaigns. Create the first one."}
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {items.map(c => {
            const eff = c.effective_status || c.status;
            const st = STATUS_STYLES[eff] || STATUS_STYLES.draft;
            const place = PLACEMENTS.find(p => p.value === c.placement);
            const ctr = c.impressions ? ((c.clicks / c.impressions) * 100).toFixed(2) : 0;
            return (
              <div key={c.id} className="surface rounded-xl overflow-hidden" data-testid={`ad-${c.id}`}>
                {c.image && <img src={c.image} alt="" className="w-full h-32 object-cover" />}
                <div className="p-4">
                  <div className="flex justify-between items-start gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      {c.advertiser_logo && <img src={c.advertiser_logo} alt="" className="w-8 h-8 rounded object-contain bg-white" />}
                      <div>
                        <div className="font-display text-base">{c.advertiser_name}</div>
                        <div className="text-xs text-[#6b6b6b]">{c.title}</div>
                      </div>
                    </div>
                    <span className="text-[10px] px-2 py-1 rounded-full border" style={{ background: st.bg, borderColor: st.border, color: st.text }}>{eff}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center text-xs my-3 py-2 border-y border-[#0a0a0a]/[0.06]">
                    <div><div className="text-[#8a8a8a]">{lang === "ar" ? "ظهور" : "Views"}</div><div className="font-medium">{(c.impressions || 0).toLocaleString()}</div></div>
                    <div><div className="text-[#8a8a8a]">{lang === "ar" ? "نقرات" : "Clicks"}</div><div className="font-medium">{(c.clicks || 0).toLocaleString()}</div></div>
                    <div><div className="text-[#8a8a8a]">CTR</div><div className="font-medium">{ctr}%</div></div>
                  </div>
                  <div className="text-xs text-[#3a3a3a] space-y-0.5">
                    <div><span className="text-[#8a8a8a]">{lang === "ar" ? "الموقع:" : "Placement:"}</span> {lang === "ar" ? (place?.ar || c.placement) : (place?.en || c.placement)}</div>
                    <div><span className="text-[#8a8a8a]">{lang === "ar" ? "المدة:" : "Period:"}</span> {c.start_date} → {c.end_date}</div>
                    <div><span className="text-[#8a8a8a]">{lang === "ar" ? "السعر:" : "Price:"}</span> {formatSAR(c.price_sar || 0)} SAR · {c.paid ? <span className="text-green-700">{lang === "ar" ? "مدفوع" : "Paid"}</span> : <span className="text-amber-700">{lang === "ar" ? "غير مدفوع" : "Unpaid"}</span>}</div>
                  </div>
                  <div className="flex gap-1 mt-3 flex-wrap">
                    <button onClick={() => openEdit(c)} className="btn-ghost text-xs" data-testid={`ad-edit-${c.id}`}><Edit3 className="w-3 h-3" /> {lang === "ar" ? "تعديل" : "Edit"}</button>
                    <button onClick={() => togglePaid(c)} className="btn-ghost text-xs">{c.paid ? (lang === "ar" ? "إلغاء الدفع" : "Mark unpaid") : (lang === "ar" ? "تأكيد الدفع" : "Mark paid")}</button>
                    {eff === "paused" ? (
                      <button onClick={() => setStatus(c, "active")} className="btn-ghost text-xs">{lang === "ar" ? "استئناف" : "Resume"}</button>
                    ) : eff === "active" ? (
                      <button onClick={() => setStatus(c, "paused")} className="btn-ghost text-xs">{lang === "ar" ? "إيقاف" : "Pause"}</button>
                    ) : null}
                    <button onClick={() => del(c.id)} className="btn-ghost text-xs text-red-700 border-red-300"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {editing && (
        <div className="fixed inset-0 bg-[#0a0a0a]/60 z-50 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-[#f1ecdf] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-[#0a0a0a]/[0.08]">
              <h3 className="font-display text-xl">{editing.id ? (lang === "ar" ? "تعديل حملة" : "Edit campaign") : (lang === "ar" ? "حملة إعلانية جديدة" : "New campaign")}</h3>
              <button onClick={close} className="text-[#6b6b6b] hover:text-[#0a0a0a]" data-testid="ad-modal-close"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={save} className="p-5 space-y-4">
              {error && <div className="text-sm text-red-700 surface rounded-lg p-3">{error}</div>}
              <fieldset className="grid sm:grid-cols-2 gap-3">
                <legend className="text-xs text-[#8a6f2e] uppercase tracking-widest mb-2 col-span-full">{lang === "ar" ? "المُعلِن" : "Advertiser"}</legend>
                <input className="input-dark" placeholder={lang === "ar" ? "اسم الشركة (مثال: STC)" : "Company (e.g. STC)"} value={editing.advertiser_name} onChange={(e) => setEditing({ ...editing, advertiser_name: e.target.value })} required data-testid="ad-advertiser-name" />
                <input className="input-dark" type="email" placeholder={lang === "ar" ? "بريد جهة التواصل" : "Contact email"} value={editing.advertiser_email || ""} onChange={(e) => setEditing({ ...editing, advertiser_email: e.target.value })} data-testid="ad-advertiser-email" />
                <input className="input-dark" placeholder={lang === "ar" ? "هاتف جهة التواصل" : "Contact phone"} value={editing.advertiser_phone || ""} onChange={(e) => setEditing({ ...editing, advertiser_phone: e.target.value })} data-testid="ad-advertiser-phone" />
                <div className="sm:col-span-2">
                  <ImageField
                    lang={lang}
                    value={editing.advertiser_logo || ""}
                    onChange={(url) => setEditing({ ...editing, advertiser_logo: url })}
                    urlPlaceholder={lang === "ar" ? "رابط شعار المُعلِن أو ارفع من جهازك" : "Advertiser logo URL or upload from device"}
                    testIdBase="ad-advertiser-logo"
                    onError={setError}
                    previewClass="w-16 h-16 rounded object-contain bg-white border"
                  />
                </div>
              </fieldset>

              <fieldset className="grid sm:grid-cols-2 gap-3">
                <legend className="text-xs text-[#8a6f2e] uppercase tracking-widest mb-2 col-span-full">{lang === "ar" ? "محتوى الإعلان" : "Creative"}</legend>
                <input className="input-dark sm:col-span-2" placeholder={lang === "ar" ? "العنوان الرئيسي" : "Headline"} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} required data-testid="ad-title" />
                <input className="input-dark sm:col-span-2" placeholder={lang === "ar" ? "العنوان الفرعي" : "Subtitle"} value={editing.subtitle || ""} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} data-testid="ad-subtitle" />
                <div className="sm:col-span-2">
                  <ImageField
                    lang={lang}
                    value={editing.image || ""}
                    onChange={(url) => setEditing({ ...editing, image: url })}
                    urlPlaceholder={lang === "ar" ? "رابط صورة البنر أو ارفع من جهازك" : "Banner image URL or upload from device"}
                    testIdBase="ad-image"
                    onError={setError}
                    required
                    previewClass="w-full h-24 object-cover rounded"
                  />
                </div>
                <input className="input-dark" placeholder={lang === "ar" ? "الرابط عند النقر" : "Click-through URL"} value={editing.link} onChange={(e) => setEditing({ ...editing, link: e.target.value })} required data-testid="ad-link" />
                <input className="input-dark" placeholder={lang === "ar" ? "نص زر الإجراء" : "CTA label"} value={editing.cta_label || ""} onChange={(e) => setEditing({ ...editing, cta_label: e.target.value })} data-testid="ad-cta" />
              </fieldset>

              <fieldset className="grid sm:grid-cols-2 gap-3">
                <legend className="text-xs text-[#8a6f2e] uppercase tracking-widest mb-2 col-span-full">{lang === "ar" ? "الجدولة والتسعير" : "Schedule & pricing"}</legend>
                <div>
                  <label className="text-xs text-[#6b6b6b] block mb-1">{lang === "ar" ? "الموقع على المنصة" : "Placement"}</label>
                  <select className="input-dark w-full" value={editing.placement} onChange={(e) => setEditing({ ...editing, placement: e.target.value })} data-testid="ad-placement">
                    {PLACEMENTS.map(p => <option key={p.value} value={p.value}>{lang === "ar" ? p.ar : p.en}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b] block mb-1">{lang === "ar" ? "الأولوية (الأعلى يظهر أولاً)" : "Priority (higher first)"}</label>
                  <input className="input-dark w-full" type="number" value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: Number(e.target.value) })} data-testid="ad-priority" />
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b] block mb-1">{lang === "ar" ? "تاريخ البداية" : "Start date"}</label>
                  <input className="input-dark w-full" type="date" value={editing.start_date} onChange={(e) => setEditing({ ...editing, start_date: e.target.value })} required data-testid="ad-start" />
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b] block mb-1">{lang === "ar" ? "تاريخ النهاية" : "End date"}</label>
                  <input className="input-dark w-full" type="date" value={editing.end_date} onChange={(e) => setEditing({ ...editing, end_date: e.target.value })} required data-testid="ad-end" />
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b] block mb-1">{lang === "ar" ? "السعر (ريال)" : "Price (SAR)"}</label>
                  <input className="input-dark w-full" type="number" step="0.01" value={editing.price_sar} onChange={(e) => setEditing({ ...editing, price_sar: Number(e.target.value) })} data-testid="ad-price" />
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b] block mb-1">{lang === "ar" ? "مرجع الفاتورة" : "Invoice ref"}</label>
                  <input className="input-dark w-full" value={editing.invoice_ref || ""} onChange={(e) => setEditing({ ...editing, invoice_ref: e.target.value })} data-testid="ad-invoice" />
                </div>
                <label className="flex items-center gap-2 text-sm sm:col-span-2 p-3 rounded-lg border border-[#0a0a0a]/[0.06] cursor-pointer">
                  <input type="checkbox" checked={!!editing.paid} onChange={(e) => setEditing({ ...editing, paid: e.target.checked })} className="w-4 h-4 accent-[#c9a55c]" data-testid="ad-paid" />
                  {lang === "ar" ? "تم استلام الدفع — جاهز للنشر" : "Payment received — ready to publish"}
                </label>
                <div className="sm:col-span-2">
                  <label className="text-xs text-[#6b6b6b] block mb-1">{lang === "ar" ? "الحالة" : "Status"}</label>
                  <select className="input-dark w-full" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="ad-status">
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </fieldset>

              <div className="flex justify-end gap-2 pt-3 border-t border-[#0a0a0a]/[0.08]">
                <button type="button" onClick={close} className="btn-ghost">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
                <button type="submit" className="btn-primary" data-testid="ad-save">{editing.id ? (lang === "ar" ? "حفظ التعديلات" : "Save") : (lang === "ar" ? "إنشاء الحملة" : "Create")}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── iter273 — Image upload helper for ad-campaign banner + logo ──────
// Wraps the existing (untouched) `POST /api/upload` endpoint so admins
// can either paste an external URL or pick a file from their device
// straight inside the campaign modal. All backend contracts, storage
// paths, and ad-campaign fields remain identical — the field still
// stores a URL string; only the way an admin *gets* that URL changed.
function ImageField({ lang, value, onChange, urlPlaceholder, testIdBase, onError, required = false, previewClass = "w-16 h-16 rounded object-contain bg-white border" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset the same input so re-picking the same file works.
    e.target.value = "";
    if (!file.type.startsWith("image/")) {
      onError?.(lang === "ar" ? "الملف يجب أن يكون صورة." : "File must be an image.");
      return;
    }
    // 10 MB soft cap — matches how big banner assets usually get before
    // the CDN starts to complain. Keeps admin from accidentally
    // uploading a 40MB print-res PSD export.
    if (file.size > 10 * 1024 * 1024) {
      onError?.(lang === "ar" ? "الحجم أكبر من 10MB." : "File exceeds 10MB.");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await apiClient.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.url) onChange(data.url);
      else onError?.(lang === "ar" ? "فشل الرفع." : "Upload failed.");
    } catch (err) {
      onError?.(err.response?.data?.detail || (lang === "ar" ? "فشل الرفع." : "Upload failed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={`${testIdBase}-field`}>
      <div className="flex gap-2 items-stretch">
        <input
          className="input-dark flex-1"
          placeholder={urlPlaceholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          data-testid={testIdBase}
        />
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
          data-testid={`${testIdBase}-file`}
        />
        <button
          type="button"
          onClick={pick}
          disabled={busy}
          className="btn-ghost text-xs shrink-0 flex items-center gap-1 disabled:opacity-60"
          data-testid={`${testIdBase}-upload-btn`}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {lang === "ar" ? "رفع" : "Upload"}
        </button>
      </div>
      {value && (
        <div className="flex items-center gap-2">
          <img
            src={value}
            alt=""
            className={previewClass}
            onError={(e) => { e.target.style.display = "none"; }}
            data-testid={`${testIdBase}-preview`}
          />
        </div>
      )}
    </div>
  );
}
